/* External */
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
  memo,
} from 'react';
import PropTypes from 'prop-types';
import debounce from 'lodash-es/debounce';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';

/* Ott-common */
import { Skeleton as CommonPreSearchSkeleton } from '@dtvgo/pre-search-card';

/* Providers */
import { useGtmLocalContext } from '../../../providers/GtmLocalProvider';

/* Components */
import LanderLayout from '../../../components/LanderLayout';
import SearchResultsSkeletons from '../../../components/SearchResultsSkeletons';
import ErrorMessage from './ErrorMessage';
import SearchResults from '../../../components/SearchResults';
import CustomKeyboard from '../../../components/CustomKeyboard';
import ScrollView from '../../../components/ScrollView';
import PreSearchContainer from '../../../components/PreSearchContainer';

/* Utils */
import useSearchResults from '../../../hooks/useSearchResults';
import { SEARCH } from '../../../utils/focusKey';
import { Direction } from '../../../utils/spatialNavigation';
import { useOnBackPress } from '../../../utils/keyDownEvents';
import withCursorVisibilityFilterFocusable from '../../../hocs/withCursorVisibilityFilter.hoc';
import { REPORT_GA, PageType } from '../../../utils/constants';
import { gaService } from '../../../services/gaService';
import { useIsSidebarExpanded, useOpenSidebar } from '../../../providers/SidebarStateContext';
import useFetchCarousel from '../../../hooks/useFetchCarousel';
import useFetchPage from '../../../hooks/useFetchPage';
import { GTM_CATEGORIES, GTM_EVENT_NAMES, GTM_SUBCATEGORIES } from '../../../utils/googleTags';
import { Routes, getLink } from '../../../utils/router';
import useQuery from '../../../hooks/useQuery';
import { getIsPagesV4OrV5Active } from '../../../utils/functions';

/* Styles */
import './Search.scss';

const MAX_PRESEARCH_CARDS = 7;
function Search({
  realFocusKey,
  cursorVisibilityChanged,
  setFocus,
}) {
  const { commonTaggingEvent } = useGtmLocalContext();
  const [resetScrollPosition, setResetScrollPosition] = useState(false);
  const isFirstTimeRender = useRef(true);
  const isSidebarExpanded = useIsSidebarExpanded();
  const searchResultsFocusKey = `${realFocusKey}-${SEARCH.SEARCH_RESULTS}`;
  const customSearchFocusKey = `${realFocusKey}-${SEARCH.CUSTOM_SEARCH}`;
  const preSearchResultsFocusKey = `${SEARCH.PRE_SEARCH}-item`;
  const goToSidebar = useOpenSidebar();
  const cursorVisibilityChangedRef = useRef(cursorVisibilityChanged);
  const history = useHistory();
  const { query } = useQuery();
  const [querySearch, setQuerySearch] = useState(query || '');
  const {
    searchResults,
    isFetching,
    areResultsLengthZero,
    minCharsToSearch,
  } = useSearchResults(querySearch);

  useEffect(() => {
    history.replace(getLink(Routes.Private.Search, { query: { query: querySearch } }));
  }, [querySearch, history]);

  useEffect(() => {
    if (!isSidebarExpanded) isFirstTimeRender.current = false;
    if (isSidebarExpanded && !isFirstTimeRender.current) {
      gaService.sendEvent({
        action: REPORT_GA.GA_EVENT_SEARCH_ABANDON,
        label: querySearch,
        category: REPORT_GA.GA_EVENT_CATEGORY_SEARCH,
      });
    }
    if (!querySearch) setResetScrollPosition(true);
  }, [isSidebarExpanded, querySearch]);

  const { page } = useFetchPage(PageType.SEARCH);
  const carouselInfo = getIsPagesV4OrV5Active() ? page?.elements : page?.carousels;

  const {
    sourceCarouselId, elementId, source, type,
  } = carouselInfo?.get(0) || {};

  const { carousel } = useFetchCarousel({
    sourceCarouselId, elementId, source, type,
  });

  const [wasPreSearchFocused] = useState(() => JSON.parse(sessionStorage.getItem('wasPreSearchFocused')));
  const [lastPreSearchCardFocusedIndex] = useState(() => sessionStorage.getItem('preSearchFocusedOn'));

  const handleBackPress = useCallback(() => {
    goToSidebar();
  }, [goToSidebar]);

  useOnBackPress({ listener: handleBackPress });

  const handleArrowPressOutCustomKeyboard = useCallback(
    (direction) => {
      if (direction === Direction.LEFT) {
        goToSidebar();
        return false;
      }
      return true;
    },
    [goToSidebar],
  );

  const { formatMessage } = useIntl();
  const inputPlaceHolder = useMemo(
    () => formatMessage({
      id: 'search.input.placeholder',
    }),
    [formatMessage],
  );

  const handleDebounceQuerySearch = useMemo(
    () => debounce(setQuerySearch, 500),
    [],
  );

  const handleChange = useCallback(
    (value) => {
      handleDebounceQuerySearch(value);
    },
    [handleDebounceQuerySearch],
  );

  cursorVisibilityChangedRef.current = cursorVisibilityChanged;

  useEffect(() => {
    if (
      wasPreSearchFocused
      && carousel?.collections?.size
      && lastPreSearchCardFocusedIndex
    ) {
      setFocus(lastPreSearchCardFocusedIndex);
    }
  }, [
    setFocus,
    lastPreSearchCardFocusedIndex,
    carousel?.collections?.size,
    preSearchResultsFocusKey,
    wasPreSearchFocused,
  ]);

  useEffect(() => {
    setResetScrollPosition(isFetching);
  }, [isFetching]);

  const searchCommonTagging = useCallback(({ eventName, subcategory, data = {} }) => {
    commonTaggingEvent({
      category: GTM_CATEGORIES.SEARCH_DATA,
      subCategory: subcategory,
      eventName,
      data: {
        searchTerms: querySearch,
        ...data,
      },
    });
  }, [commonTaggingEvent, querySearch]);

  const handleClearSearchTagEvent = useCallback(() => {
    searchCommonTagging({
      eventName: GTM_EVENT_NAMES.SEARCH.CLEAR_SEARCH,
      subcategory:
        searchResults
          ? GTM_SUBCATEGORIES.SEARCH.SEARCH_RESULT
          : GTM_SUBCATEGORIES.SEARCH.SEARCH_DEFAULT_SCREEN,
    });
  }, [searchCommonTagging, searchResults]);

  const handleSearchDefaultContentTagEvent = useCallback(({ data }) => {
    searchCommonTagging({
      eventName: GTM_EVENT_NAMES.SEARCH.DEFAULT_CONTENT,
      subcategory: GTM_SUBCATEGORIES.SEARCH.SEARCH_DEFAULT_SCREEN,
      data,
    });
  }, [searchCommonTagging]);

  const handleSearchResultTagEvent = useCallback(({ data }) => {
    searchCommonTagging({
      eventName: GTM_EVENT_NAMES.SEARCH.RELATED_SEARCH_RESULT,
      subcategory: GTM_SUBCATEGORIES.SEARCH.SEARCH_RESULT,
      data,
    });
  }, [searchCommonTagging]);

  const handleSearchNotFoundEvent = useCallback(() => {
    if (areResultsLengthZero && querySearch?.trim()?.length >= minCharsToSearch) {
      searchCommonTagging({
        eventName: GTM_EVENT_NAMES.SEARCH.RELATED_SEARCH_RESULT_NOT_FOUND,
        subcategory: GTM_SUBCATEGORIES.SEARCH.SEARCH_NOT_FOUND,
      });
    }
  }, [areResultsLengthZero, minCharsToSearch, querySearch, searchCommonTagging]);

  useEffect(() => {
    handleSearchNotFoundEvent();
  }, [handleSearchNotFoundEvent]);

  const preSearchSkeleton = useMemo(
    () => (
      <div className="dtv-html5-c-search__pre-search-skeleton">
        {Array.from({ length: MAX_PRESEARCH_CARDS })
          .fill()
          .map((_, index) => (
            <div
              className="dtv-html5-c-search__pre-search-skeleton-container-card"
              // eslint-disable-next-line react/no-array-index-key -- no better key
              key={`pre-search-skeleton-card-${index}`}
            >
              <CommonPreSearchSkeleton className="dtv-html5-c-search__pre-search-skeleton-card" />
            </div>
          ))}
      </div>
    ),
    [],
  );

  const showPresearch = useMemo(
    () => !searchResults && !isFetching,
    [isFetching, searchResults],
  );
  const hasNoContentToShow = useMemo(() => searchResults?.size === 0, [searchResults]);

  const searchTitle = useMemo(
    () => (
      <p className="dtv-html5-c-search__title">
        <FormattedMessage
          id={showPresearch ? 'search.preSearch.title' : 'search.results.title'}
        />
      </p>
    ),
    [showPresearch],
  );

  const preSearchCardsContainers = useMemo(
    () => (
      <PreSearchContainer
        focusKey={preSearchResultsFocusKey}
        carousel={carousel}
        pageId={PageType.SEARCH}
        handleSearchDefaultContentTagEvent={handleSearchDefaultContentTagEvent}
      />
    ),
    [preSearchResultsFocusKey, carousel, handleSearchDefaultContentTagEvent],
  );

  return (
    <LanderLayout className="dtv-html5-c-search">
      <div className="dtv-html5-c-search__flex-container">
        <div className="dtv-html5-c-search__header">
          <CustomKeyboard
            focusKey={customSearchFocusKey}
            onChange={handleChange}
            inputPlaceholder={inputPlaceHolder}
            onArrowPressOut={handleArrowPressOutCustomKeyboard}
            handleClearSearchTagEvent={handleClearSearchTagEvent}
            initialValue={querySearch}
          />
        </div>
        <ScrollView
          className={hasNoContentToShow ? 'dtv-html5-c-search__without-content' : 'dtv-html5-c-search__content'}
          resetScrollPosition={resetScrollPosition}
        >
          {(showPresearch || !hasNoContentToShow) && searchTitle}

          {isFetching && (
            <div className="dtv-html5-c-search__results-container-skeletons">
              <SearchResultsSkeletons />
            </div>
          )}
          {showPresearch && (
            <div className="dtv-html5-c-search__pre-search-container">
              {carousel?.collections?.size > 0
                ? preSearchCardsContainers
                : preSearchSkeleton}
            </div>
          )}
          <div className="dtv-html5-c-search__results-container">
            {hasNoContentToShow ? (
              <ErrorMessage />
            ) : (
              <SearchResults
                focusKey={searchResultsFocusKey}
                searchResults={searchResults}
                querySearch={querySearch}
                handleSearchResultTagEvent={handleSearchResultTagEvent}
              />
            )}
          </div>
        </ScrollView>
      </div>
    </LanderLayout>
  );
}

Search.propTypes = {
  realFocusKey: PropTypes.string.isRequired,
  setFocus: PropTypes.func.isRequired,
  cursorVisibilityChanged: PropTypes.bool.isRequired,
};

export default withCursorVisibilityFilterFocusable()(memo(Search));
